// The Pilot card (settings.pilot, see lib/pilot.js), at the top of the KPIs
// page ("Pilot KPI Tracker"), the pilot's home: start a pilot, see which phase
// it is in, pick the 2 or 3 team metrics it is judged on, compare them before
// and after day 15, end it. The Control Room shows only the
// baseline banner (PilotBaselineBanner), which links here.
//
// The card draws; usePilotData loads. Everything from the server comes
// through fetchPilotMetrics (GET /api/pilot/status, lib/pilot.js): the
// server's own phase and day, which win over the client's calendar, and the
// team metrics from its daily snapshots.
import React from "react";
import { Link } from "react-router-dom";
import CrIcon from "./CrIcon";
import FancyDate from "./FancyDate";
import { getAllEvents, summarize } from "../lib/kpi";
import {
  PILOT_BASELINE_DAYS,
  addCalendarDays,
  PILOT_LENGTH_DAYS,
  PILOT_MAX_KPIS,
  PILOT_MIN_KPIS,
  PILOT_PHASE_LABELS,
  endPilot,
  fetchMemorySummary,
  fetchPilotMetrics,
  formatMetricValue,
  formatPilotDate,
  isYmd,
  metricChange,
  pilotMetricOptions,
  pilotRunWindow,
  pilotShape,
  resolvePilotState,
  startPilot,
  statusMatchesPilot,
  togglePilotKpi,
} from "../lib/pilot";
import { dialog } from "../lib/dialog";

const PHASE_TONE = {
  scheduled: "",
  baseline: "warn",
  running: "ok",
  readout: "ok",
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Loads what the Pilot card shows for an active pilot: the server's status
 * (its phase, and the team metrics), and BossDrop's own activity since day 15.
 * `events`: KPI events the page already has (the KPIs page), so they are not
 * read twice; leave it undefined to load them. `withActivity: false` skips
 * the activity entirely (the Control Room's banner needs only the phase).
 * `saveVersion` goes up each time the page's settings save lands: an answer
 * that still describes the previous pilot setting (Start or End not yet
 * saved when it was read) is set aside and read again after the save.
 * `learned` is what BossDrop has learned so far (GET /api/memory/summary), the
 * thing the readout is really about. `loadMetrics`, `loadEvents` and
 * `loadMemory` are injectable for tests and screenshots.
 */
export function usePilotData({
  pilot,
  today,
  apiBase,
  ownerEmail,
  saveVersion = 0,
  events: givenEvents,
  withActivity = true,
  loadMetrics = fetchPilotMetrics,
  loadEvents = getAllEvents,
  loadMemory = fetchMemorySummary,
}) {
  const p = pilotShape(pilot);
  const active = !!(p && !p.endedAt);
  const [status, setStatus] = React.useState(null);
  const [loadState, setLoadState] = React.useState("idle");
  const [loadedEvents, setEvents] = React.useState(null);
  const eventsGiven = givenEvents !== undefined;
  const events = eventsGiven ? givenEvents : loadedEvents;
  const pilotKey = p ? `${p.startDate}|${p.endedAt ? "ended" : ""}` : "";
  const fresh = active && statusMatchesPilot(status, p);

  const load = React.useCallback(() => {
    let alive = true;
    setLoadState("loading");
    Promise.resolve(loadMetrics({ apiBase }))
      .then((data) => {
        if (!alive) return;
        setStatus(data || null);
        setLoadState(data ? "ready" : "unavailable");
      })
      .catch(() => {
        if (!alive) return;
        setStatus(null);
        setLoadState("unavailable");
      });
    return () => {
      alive = false;
    };
  }, [apiBase, loadMetrics]);

  // A new or changed pilot: read the server's answer.
  React.useEffect(() => {
    if (!active) {
      setLoadState("idle");
      return undefined;
    }
    return load();
  }, [active, pilotKey, load]);

  // After a save lands, read again if the answer was for the old setting.
  const freshRef = React.useRef(fresh);
  freshRef.current = fresh;
  React.useEffect(() => {
    if (!saveVersion || !active || freshRef.current) return undefined;
    return load();
  }, [saveVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = fresh ? status : null;
  const metricsStatus =
    loadState === "ready" && !fresh ? "loading" : loadState;
  const effectiveToday = current?.today || today;
  const state = resolvePilotState({ pilot: p, today: effectiveToday, server: current });
  const runWindow = pilotRunWindow(state, effectiveToday);
  const wantsEvents = !!(withActivity && !eventsGiven && runWindow && ownerEmail);

  React.useEffect(() => {
    if (!wantsEvents) return undefined;
    let alive = true;
    Promise.resolve(loadEvents(ownerEmail))
      .then((list) => {
        if (alive) setEvents(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (alive) setEvents(null);
      });
    return () => {
      alive = false;
    };
  }, [wantsEvents, ownerEmail, loadEvents]);

  const [learned, setLearned] = React.useState(null);
  React.useEffect(() => {
    if (!active || !withActivity) {
      setLearned(null);
      return undefined;
    }
    let alive = true;
    Promise.resolve(loadMemory({ apiBase }))
      .then((data) => {
        if (alive) setLearned(data || null);
      })
      .catch(() => {
        if (alive) setLearned(null);
      });
    return () => {
      alive = false;
    };
  }, [active, withActivity, pilotKey, apiBase, loadMemory]);

  const activity = React.useMemo(() => {
    if (!withActivity || !runWindow || !Array.isArray(events)) return null;
    const s = summarize(events, runWindow.start, runWindow.end);
    return {
      pings: s.pingsSent,
      escalations: s.escalationsSent,
      tasksCreated: s.tasksCreated,
    };
  }, [withActivity, events, runWindow?.start, runWindow?.end]); // eslint-disable-line react-hooks/exhaustive-deps

  return { state, metrics: current, metricsStatus, activity, learned };
}

/**
 * The Automations card's notice while the pilot is in its baseline. `link` is
 * the caller's link to the Pilot card on the KPIs page (a router Link, kept
 * out of this file so it renders without a router in tests).
 */
export function PilotBaselineBanner({ state, link = null }) {
  if (state?.phase !== "baseline") return null;
  return (
    <div className="infoBanner pilotBanner" role="note">
      <strong>Pilot baseline:</strong> BossDrop is watching only until{" "}
      {formatPilotDate(state.baselineEndsOn)}. Automations start on day{" "}
      {PILOT_BASELINE_DAYS + 1} ({formatPilotDate(state.runStartsOn)}). Direct
      requests by email or chat still work.
      {link ? <> {link}</> : null}
    </div>
  );
}

function phaseNote(state) {
  switch (state.phase) {
    case "scheduled":
      return `The pilot starts on ${formatPilotDate(state.startDate)}. Until then BossDrop runs as usual.`;
    case "baseline":
      return `Watching only until ${formatPilotDate(state.baselineEndsOn)}: BossDrop records how the team's work is going for this page. It sends nothing: no reports, nudges or escalations.`;
    case "running":
      return `BossDrop is running on its own until ${formatPilotDate(state.endsOn)}, then the readout. It went to Automated on day ${PILOT_BASELINE_DAYS + 1}; you can send any automation back to approval in Control Room.`;
    case "readout":
      return "Readout: compare the results with the baseline, see what BossDrop learned about how your team runs, and decide.";
    default:
      return "";
  }
}

/**
 * What BossDrop learned during the pilot: the moment the buyer sees what they
 * would be walking away from. Its numbers, the most telling lines, and the way
 * to the whole memory.
 */
function WhatItLearned({ learned, phase }) {
  const stats = learned?.stats || {};
  const lines = Number(stats.linesLearned) || 0;
  const moments = Number(stats.moments) || 0;
  const used = Number(stats.usedTotal) || 0;
  const highlights = Array.isArray(learned?.highlights) ? learned.highlights : [];
  if (!learned || (!lines && !moments)) return null;
  const parts = [];
  if (lines) parts.push(`${plural(lines, "thing")} about how your team runs`);
  const from = moments ? `from ${moments.toLocaleString("en-US")} moments of real work` : "";
  const did = used ? ` It used them in ${plural(used, "decision")}.` : "";
  return (
    <div className="pilotCard__learned">
      <div className="crFieldLabel">What BossDrop learned</div>
      <p className="small pilotCard__learnedLead">
        {lines
          ? `It has learned ${parts[0]}${from ? `, ${from}` : ""}.${did}`
          : `It is watching: ${moments.toLocaleString("en-US")} moments of real work so far.`}
        {lines && phase === "readout" ? " This is how your company runs, as BossDrop now knows it." : ""}
      </p>
      {highlights.length ? (
        <ul className="pilotCard__learnedLines">
          {highlights.map((h) => (
            <li key={h.ref || h.text}>{h.text}</li>
          ))}
        </ul>
      ) : null}
      <Link className="pilotCard__learnedLink" to="/memory">
        See all of it on the Memory page
      </Link>
    </div>
  );
}

function collectingText(state, metrics, metricsStatus) {
  if (metricsStatus === "loading") return "Loading the team metrics…";
  if (metricsStatus !== "ready" || !metrics) {
    return "The team metrics aren't available yet.";
  }
  if (state.phase === "scheduled") {
    return `Snapshots start on ${formatPilotDate(state.startDate)}.`;
  }
  const c = metrics.collected || {};
  const days = (c.baselineDays || 0) + (c.runDays || 0) + (c.readoutDays || 0);
  // The server snapshots once a day at about 4 PM in the workspace's zone.
  if (days === 0) return "First snapshot today after 4 PM.";
  const partial = c.partialDays || 0;
  return `Collecting since ${formatPilotDate(state.startDate)}, ${plural(days, "day")} so far.${
    partial
      ? ` ${plural(partial, "day")} with a tool outage left out of the averages.`
      : ""
  }`;
}

function PhaseRanges({ state }) {
  const items = [
    {
      key: "baseline",
      label: "Baseline",
      text: `${formatPilotDate(state.startDate)} to ${formatPilotDate(state.baselineEndsOn)}`,
    },
    {
      key: "running",
      label: "Running",
      text: `${formatPilotDate(state.runStartsOn)} to ${formatPilotDate(state.endsOn)}`,
    },
    {
      key: "readout",
      label: "Readout",
      text: `From ${formatPilotDate(addCalendarDays(state.endsOn, 1))}`,
    },
  ];
  return (
    <div className="pilotPhases">
      {items.map((it) => (
        <div
          key={it.key}
          className={`pilotPhase${state.phase === it.key ? " is-current" : ""}`}
          aria-current={state.phase === it.key ? "step" : undefined}
        >
          <div className="pilotPhase__label">{it.label}</div>
          <div className="pilotPhase__dates">{it.text}</div>
        </div>
      ))}
    </div>
  );
}

function Comparison({ state, metrics, selected }) {
  const byKey = Object.fromEntries(
    (Array.isArray(metrics?.metrics) ? metrics.metrics : []).map((m) => [m.key, m]),
  );
  const options = pilotMetricOptions(metrics);
  const rows = selected.map(
    (key) =>
      byKey[key] || {
        ...(options.find((o) => o.key === key) || { key, label: key }),
        prePilot: null,
        baseline: null,
        run: null,
      },
  );
  const showBefore = rows.some((r) => r.prePilot != null);
  const beforeRun = state.phase === "scheduled" || state.phase === "baseline";
  const cols = [
    ...(showBefore ? [{ key: "prePilot", label: "Before the pilot" }] : []),
    { key: "baseline", label: `Baseline (days 1 to ${PILOT_BASELINE_DAYS})` },
    { key: "run", label: `Since day ${PILOT_BASELINE_DAYS + 1}` },
    { key: "change", label: "Change" },
  ];
  return (
    <div
      className="pilotCmp"
      role="table"
      aria-label="Pilot comparison"
      style={{ "--pilot-cols": cols.length }}
    >
      <div className="pilotCmp__row pilotCmp__head" role="row">
        <div className="pilotCmp__metric" role="columnheader">
          Metric
        </div>
        {cols.map((c) => (
          <div key={c.key} className="pilotCmp__cell" role="columnheader">
            {c.label}
          </div>
        ))}
      </div>
      {rows.map((m) => {
        const change = beforeRun ? { text: "—", tone: "neutral" } : metricChange(m);
        const cell = (key) => {
          if (key === "change") {
            return (
              <span className={`pilotCmp__change is-${change.tone}`}>{change.text}</span>
            );
          }
          if (key === "baseline" && state.phase === "scheduled") {
            return <span className="muted">Starts {formatPilotDate(state.startDate)}</span>;
          }
          if (key === "run" && beforeRun) {
            return <span className="muted">Starts day {PILOT_BASELINE_DAYS + 1}</span>;
          }
          return formatMetricValue(m[key], m.format);
        };
        return (
          <div key={m.key} className="pilotCmp__row" role="row" data-metric={m.key}>
            <div className="pilotCmp__metric" role="rowheader">
              {m.label}
            </div>
            {cols.map((c) => (
              <div key={c.key} className="pilotCmp__cell" role="cell">
                <span className="pilotCmp__colLabel" aria-hidden="true">
                  {c.label}
                </span>
                <span className="pilotCmp__value">{cell(c.key)}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default function PilotCard({
  pilot,
  onChange,
  canManage = false,
  today,
  state,
  metrics = null,
  metricsStatus = "idle",
  activity = null,
  learned = null,
  // While a change is being saved its controls wait; a failed save says so.
  busy = false,
  saveError = "",
}) {
  const p = pilotShape(pilot);
  const active = !!(p && !p.endedAt && state && state.phase !== "ended");
  const [startInput, setStartInput] = React.useState(today);
  React.useEffect(() => {
    setStartInput((cur) => (isYmd(cur) ? cur : today));
  }, [today]);

  const start = async () => {
    if (!canManage || busy || !isYmd(startInput)) return;
    const ok = await dialog.confirm({
      title: `Start the pilot on ${formatPilotDate(startInput)}?`,
      body: `For the first ${PILOT_BASELINE_DAYS} days BossDrop watches only: it collects the numbers for this page and sends no reports, nudges or escalations. From day ${PILOT_BASELINE_DAYS + 1} it switches to Automated and runs on its own.`,
      confirmLabel: "Start pilot",
    });
    if (!ok) return;
    onChange?.(startPilot(startInput, p));
  };
  const end = async () => {
    if (!canManage || busy) return;
    const ok = await dialog.confirm({
      title: "End pilot mode?",
      body: "BossDrop keeps running fully. The pilot's dates and metric choices are kept, and you can start a new pilot later.",
      confirmLabel: "End pilot",
    });
    if (!ok) return;
    onChange?.(endPilot(p));
  };
  const toggleKpi = (key) => {
    if (!canManage || busy || !p) return;
    const next = togglePilotKpi(p.kpis, key);
    if (next !== p.kpis) onChange?.({ ...p, kpis: next });
  };

  return (
    <div className="panelSub pilotCard" id="pilot">
      <div className="pilotCard__head">
        <h3 className="crTitle" style={{ margin: 0 }}>
          <CrIcon name="tracking" size={18} />
          Pilot
        </h3>
        {active ? (
          <div className="pilotCard__status">
            <span className="pilotCard__day">
              {state.phase === "scheduled"
                ? `Starts ${formatPilotDate(state.startDate)}`
                : `Day ${state.day} of ${state.lengthDays}`}
            </span>
            <span className={`chip ${PHASE_TONE[state.phase] || ""}`.trim()}>
              {PILOT_PHASE_LABELS[state.phase]}
            </span>
          </div>
        ) : null}
      </div>

      {!active ? (
        <>
          <p className="muted" style={{ marginTop: 4 }}>
            Run BossDrop as a {PILOT_LENGTH_DAYS}-day pilot. Days 1 to{" "}
            {PILOT_BASELINE_DAYS} are the baseline: BossDrop watches only. It
            records how the team's work is going for this page and sends no
            reports, nudges or escalations. From day{" "}
            {PILOT_BASELINE_DAYS + 1} it switches to Automated and runs on its own. On day {PILOT_LENGTH_DAYS}{" "}
            the results are compared with the baseline and you decide.
          </p>
          {p?.endedAt ? (
            <p className="muted small" style={{ margin: "0 0 8px" }}>
              The last pilot started on {formatPilotDate(p.startDate)} and was
              ended on {new Date(p.endedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}.
            </p>
          ) : null}
          <div className="pilotCard__start">
            <label className="crField pilotCard__date">
              <span className="crFieldLabel">Start date</span>
              <FancyDate
                value={startInput || ""}
                onChange={setStartInput}
                disabled={!canManage}
                ariaLabel="Start date"
              />
            </label>
            <button
              type="button"
              className="btn primary"
              onClick={start}
              disabled={!canManage || busy || !isYmd(startInput)}
            >
              Start pilot
            </button>
          </div>
        </>
      ) : (
        <>
          <PhaseRanges state={state} />
          <p className="muted small pilotCard__note">{phaseNote(state)}</p>

          <div className="crSubGroup">
            <div className="pilotCard__subhead">
              <div className="crFieldLabel">Team metrics to compare</div>
              <div className="muted small">
                Pick {PILOT_MIN_KPIS} or {PILOT_MAX_KPIS}.{" "}
                {collectingText(state, metrics, metricsStatus)}
              </div>
            </div>
            <div className="pilotKpis" role="group" aria-label="Team metrics to compare">
              {pilotMetricOptions(metrics).map((m) => {
                const checked = p.kpis.includes(m.key);
                const locked = checked
                  ? p.kpis.length <= PILOT_MIN_KPIS
                  : p.kpis.length >= PILOT_MAX_KPIS;
                return (
                  <label
                    key={m.key}
                    className={`crTile crOptionCard crOptionCard--inline pilotKpi${
                      checked ? " is-selected" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="bd-check"
                      checked={checked}
                      disabled={!canManage || busy || locked}
                      onChange={() => toggleKpi(m.key)}
                    />
                    <span>{m.label}</span>
                  </label>
                );
              })}
            </div>

            <Comparison state={state} metrics={metrics} selected={p.kpis} />

            {activity ? (
              <p className="muted small pilotCard__activity">
                What BossDrop did since day {PILOT_BASELINE_DAYS + 1}:{" "}
                {plural(activity.pings, "status ping")},{" "}
                {plural(activity.escalations, "escalation")},{" "}
                {plural(activity.tasksCreated, "task")} created.
              </p>
            ) : null}

            <WhatItLearned learned={learned} phase={state.phase} />
          </div>

          <div className="pilotCard__actions">
            <button
              type="button"
              className="btn"
              onClick={end}
              disabled={!canManage || busy}
            >
              End pilot mode
            </button>
          </div>
        </>
      )}
      {saveError ? (
        <p className="small pilotCard__error" role="alert">
          {saveError}
        </p>
      ) : null}
      {!canManage ? (
        <p className="muted small" style={{ margin: "10px 0 0" }}>
          Only admins can change the pilot.
        </p>
      ) : null}
    </div>
  );
}
